import { CoreButtonView, CoreTextView, TCoreButtonStyleProps, type TCoreButtonViewProps } from '@core/widgets';
import { useBehavior, useButton, useTriggerAction } from '@libs/core';
import { TWidgetProps } from '@libs/types';
import React from 'react';
import { SemanticColor, semanticColors } from '../../theme/colors';
import { TMenuButtonData } from '../../types/data';
import { TMenuButtonOptions } from '../../types/options';
import { VisualView } from '../Image/VisualView';
import { Tag } from '../UI/Tag/Tag';
import styles from './styles/buttonMenu.module.css';

export const ButtonMenu = ({ widget }: TWidgetProps<TMenuButtonData, TMenuButtonOptions>) => {
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { handleClick } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
    const data = widget.data ?? {};
    const messages = data.messages ?? {};
    const options = widget.options ?? {};
    const hasIconOnRight = options.iconPosition === 'right' && options.iconName;
    const hasIconOnLeft = options.iconPosition !== 'right' && options.iconName;

    const coreButtonStyleProps: TCoreButtonStyleProps = {
        main: semanticColors[SemanticColor.FillTertiary],
        onMain: semanticColors[SemanticColor.TextPrimary],
        fillContainer: true,
        borderRadius: 0,
        padding: hasIconOnLeft ? { top: 16, right: 24, bottom: 16, left: 16 } : { top: 16, right: 24, bottom: 16, left: 24 },
        fontStyle: { fontSize: 17, lineHeight: 24 },
        margin: options.margin,
        cursorDisabled: 'default',
        textAlign: 'start',
        disabled: {
            onMain: semanticColors[SemanticColor.TextPrimaryDisabled],
        },
        hovered: {
            main: semanticColors[SemanticColor.BgPrimaryHovered],
        },
        pressed: {
            main: semanticColors[SemanticColor.BgPrimaryPressed],
        },
    };

    const parentStyleNames = [options.style ?? ' '].filter(Boolean) as string[];

    return (
        <CoreButtonView
            widget={widget as TCoreButtonViewProps['widget']}
            text={messages.label}
            isDisabled={isDisabled}
            isHide={isHidden}
            styleProps={coreButtonStyleProps}
            parentStyleNames={parentStyleNames}
            styleName="buttonMenu"
            onClick={() => handleClick()}
        >
            {hasIconOnLeft && options.iconName && (
                <VisualView
                    iconName={options.iconName}
                    parentStyleNames={parentStyleNames}
                    styleName="buttonMenuIcon"
                    size="small"
                    isDisabled={isDisabled}
                    iconColor={isDisabled ? semanticColors[SemanticColor.IconEmphasisedDisabled] : semanticColors[SemanticColor.IconEmphasised]}
                />
            )}
            <div className={styles.content}>
                {messages.label && (
                    <CoreTextView
                        text={messages.label}
                        as="span"
                        parentStyleNames={parentStyleNames}
                        styleName={'buttonMenuText'}
                        isDisabled={isDisabled}
                        styleProps={{
                            main: semanticColors[SemanticColor.TextPrimary],
                            disabled: {
                                main: semanticColors[SemanticColor.TextPrimaryDisabled],
                            },
                            maxLines: 1,
                            textAlign: 'start',
                        }}
                    />
                )}
            </div>
            {hasIconOnRight && options.iconName && (
                <VisualView
                    iconName={options.iconName}
                    parentStyleNames={parentStyleNames}
                    styleName="buttonMenuIcon"
                    size="small"
                    isDisabled={isDisabled}
                    iconColor={isDisabled ? semanticColors[SemanticColor.IconEmphasisedDisabled] : semanticColors[SemanticColor.IconEmphasised]}
                />
            )}
            {!hasIconOnRight && messages.metaText && (
                <Tag
                    metaText={messages.metaText}
                    metaHierarchy="secondary"
                    parentStyleNames={parentStyleNames}
                    styleName="buttonMenuMeta"
                    isDisabled={isDisabled}
                />
            )}
        </CoreButtonView>
    );
};
